// ADR-132: add / edit one job position mapping.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Alert,
  Chip,
  Box,
  CircularProgress,
} from '@mui/material';
import PositionSelector from '../../common/PositionSelector';
import { POSITION_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import {
  INGESTION_MAPPING_OUTCOMES,
  INGESTION_PATTERN_MATCH_TYPE_LABELS,
  INGESTION_SOURCE_PATTERN_MAX_LENGTH,
} from '../../../constants/ingestion';
import {
  classifySourcePattern,
  describeSourcePattern,
  validateMappingDraft,
} from '../../../utils/ingestionMappings';

const EMPTY_DRAFT = {
  sourcePattern: '',
  outcome: INGESTION_MAPPING_OUTCOMES.MAP_TO_POSITION,
  positionId: '',
  notes: '',
};

// ADR-133 D11: `initialPattern` pre-fills a new mapping ("Map this title").
const MappingDialog = ({
  open, mapping, initialPattern = '', providerLabel, sourceLabel, onClose, onSave,
}) => {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    if (open) {
      setDraft(mapping
        ? {
          sourcePattern: mapping.sourcePattern,
          outcome: mapping.outcome,
          positionId: mapping.position?.id || '',
          notes: mapping.notes || '',
        }
        : { ...EMPTY_DRAFT, sourcePattern: initialPattern || '' });
      setSubmitted(false);
      setServerError(null);
      setSaving(false);
    }
  }, [open, mapping, initialPattern]);

  const { errors, warnings } = useMemo(() => validateMappingDraft(draft), [draft]);
  const matchType = classifySourcePattern(draft.sourcePattern);

  const update = (field, value) => {
    setServerError(null);
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (errors.length > 0) {
      return;
    }
    try {
      setSaving(true);
      await onSave({
        sourcePattern: draft.sourcePattern,
        outcome: draft.outcome,
        positionId: draft.outcome === INGESTION_MAPPING_OUTCOMES.MAP_TO_POSITION ? draft.positionId : null,
        notes: draft.notes,
      });
    } catch (error) {
      setServerError(error?.message || 'Failed to save the mapping');
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mapping ? 'Edit mapping' : 'Add mapping'} · {providerLabel}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          label={`Source title or pattern (${sourceLabel})`}
          value={draft.sourcePattern}
          onChange={(e) => update('sourcePattern', e.target.value)}
          fullWidth
          autoFocus
          margin="dense"
          inputProps={{ maxLength: INGESTION_SOURCE_PATTERN_MAX_LENGTH }}
          error={submitted && !draft.sourcePattern.trim()}
          helperText="Use * as a wildcard: *Engineer* (contains), Senior* (starts with). Exact titles win over patterns; the most specific pattern wins. Matching ignores case."
        />
        {matchType && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, mb: 2 }}>
            <Chip size="small" label={INGESTION_PATTERN_MATCH_TYPE_LABELS[matchType]} />
            <span style={{ fontSize: 13, color: '#64748B' }}>{describeSourcePattern(draft.sourcePattern)}</span>
          </Box>
        )}

        <FormLabel sx={{ mt: 2, display: 'block' }}>Result</FormLabel>
        <RadioGroup
          value={draft.outcome}
          onChange={(e) => update('outcome', e.target.value)}
        >
          <FormControlLabel
            value={INGESTION_MAPPING_OUTCOMES.MAP_TO_POSITION}
            control={<Radio size="small" />}
            label="Map to a Radar position"
          />
          <FormControlLabel
            value={INGESTION_MAPPING_OUTCOMES.NO_POSITION}
            control={<Radio size="small" />}
            label="No position (the consultant is synced without a position; no automatic matching)"
          />
        </RadioGroup>

        {draft.outcome === INGESTION_MAPPING_OUTCOMES.MAP_TO_POSITION && (
          <Box sx={{ mt: 1 }}>
            <PositionSelector
              label="Radar position"
              value={draft.positionId}
              returnValue={POSITION_RETURN_TYPES.ID}
              onChange={(value) => update('positionId', value)}
              required
              size="small"
            />
          </Box>
        )}

        <TextField
          label="Notes (optional)"
          value={draft.notes}
          onChange={(e) => update('notes', e.target.value)}
          fullWidth
          multiline
          minRows={2}
          margin="normal"
          inputProps={{ maxLength: 1000 }}
        />

        {submitted && errors.map((message) => (
          <Alert key={message} severity="error" sx={{ mt: 1 }}>{message}</Alert>
        ))}
        {warnings.map((message) => (
          <Alert key={message} severity="warning" sx={{ mt: 1 }}>{message}</Alert>
        ))}
        {serverError && <Alert severity="error" sx={{ mt: 1 }}>{serverError}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MappingDialog;
